import { useState } from 'react';
import { toast } from 'react-toastify';
import { Check, Copy } from 'lucide-react';
import Modal from '../Modal';
import { copyToClipboard, formatDateTime } from '../../lib/domain-expert-api';
import { btnPrimary, btnSecondary, inputClass } from './ui';

export interface LinkModalData {
  kind: 'invite' | 'reset';
  url: string;
  expiresAt: string | null;
  email: string;
  /** null when no email was attempted (the admin asked for a link to copy). */
  emailSent: boolean | null;
}

export default function InviteLinkModal({ data, onClose }: { data: LinkModalData | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  if (!data) return null;

  const isInvite = data.kind === 'invite';
  const title = isInvite ? 'Invite link' : 'Password reset link';

  let message: string;
  if (data.emailSent === false) {
    message = "We couldn't send the email. Copy the link and send it yourself.";
  } else if (data.emailSent === true) {
    message = isInvite
      ? `We emailed an invite to ${data.email}. You can also copy the link and send it yourself.`
      : `We emailed a password reset link to ${data.email}. You can also copy the link and send it yourself.`;
  } else {
    message = `Copy this link and send it to ${data.email}.`;
  }

  const handleCopy = () => {
    copyToClipboard(data.url).then((ok) => {
      if (ok) {
        setCopied(true);
        toast.success('Link copied');
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error("Couldn't copy. Select the link and copy it.");
      }
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <button type="button" onClick={onClose} className={btnPrimary}>
          Done
        </button>
      }
    >
      <div className="space-y-4">
        <p className={`text-sm ${data.emailSent === false ? 'text-amber-700' : 'text-gray-600'}`}>{message}</p>
        <div className="flex gap-2">
          <input
            readOnly
            aria-label={title}
            value={data.url}
            onFocus={(e) => e.target.select()}
            className={`${inputClass} font-mono text-xs text-gray-700`}
          />
          <button type="button" onClick={handleCopy} className={btnSecondary}>
            {copied ? <Check className="mr-1.5 h-4 w-4 text-green-600" /> : <Copy className="mr-1.5 h-4 w-4" />}
            Copy
          </button>
        </div>
        {data.expiresAt && (
          <p className="text-xs text-gray-500">
            {isInvite ? 'This link can be used once' : 'This link works'} until {formatDateTime(data.expiresAt)}.
          </p>
        )}
      </div>
    </Modal>
  );
}
